import {
  type Database as _Database,
  get,
  onValue,
  ref,
  type Unsubscribe as _Unsubscribe,
  query as _query,
  onChildChanged,
  onChildAdded,
} from 'firebase/database'

import { applyQueryFilter, Filter } from './filter'
import { GetSnapshotReturnType } from './util'
import { database as clientDatabase } from '../client-app'

export type Database = _Database
export type Unsubscribe = _Unsubscribe
export class FirebaseDatabase {
  database: Database

  constructor(database: Database = clientDatabase) {
    this.database = database
  }

  getValue = async <T>({
    path,
    filters,
  }: {
    path: string
    filters?: Filter
  }): Promise<GetSnapshotReturnType<T | null>> => {
    try {
      let dataQuery = _query(ref(this.database, path))

      if (filters) {
        dataQuery = applyQueryFilter(dataQuery, filters)
      }

      const snapshot = await get(dataQuery)

      if (!snapshot.exists()) {
        return { success: true, data: null }
      }

      return { success: true, data: snapshot.val() as T }
    } catch (error) {
      console.error(`Error fetching data from ${path}:`, error)

      return { success: false, error }
    }
  }

  /* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
  subscribe = <T>({
    path,
    filters,
    onUpdate,
    onCancel,
  }: {
    path: string
    filters?: Filter
    onUpdate: (data: T) => void
    onCancel?: (error: Error) => void
  }) => {
    try {
      let query = _query(ref(this.database, path))

      if (filters) {
        query = applyQueryFilter(query, filters)
      }

      const unsubscribe = onValue(
        query,
        snapshot => {
          const data = snapshot.val()

          if (data) {
            onUpdate(data as T)
          }
        },
        onCancel,
      )

      return unsubscribe
    } catch (error) {
      console.error(`Error subscribing to snapshot from ${path}:`, error)

      return null
    }
  }

  onChildAdded = <T>({
    path,
    filters,
    onUpdate,
    onError,
  }: {
    path: string
    filters?: Filter
    onUpdate: (key: number, data: T) => void
    onError?: (error: Error) => void
  }) => {
    let dataQuery = _query(ref(this.database, path))

    if (filters) {
      dataQuery = applyQueryFilter(dataQuery, filters)
    }

    return onChildAdded(
      dataQuery,
      snapshot => {
        const data = snapshot.val()
        const dataKey = snapshot.key

        if (data && dataKey) {
          onUpdate(Number(dataKey), data as T)
        }
      },
      onError,
    )
  }

  onChildChanged = <T>({
    path,
    onUpdate,
    onError,
  }: {
    path: string
    onUpdate: (key: number, data: T) => void
    onError?: (error: Error) => void
  }) => {
    const dataRef = ref(this.database, path)

    return onChildChanged(
      dataRef,
      snapshot => {
        const data = snapshot.val()
        const dataKey = snapshot.key

        if (data && dataKey) {
          onUpdate(Number(dataKey), data as T)
        }
      },
      onError,
    )
  }
}
